// O QUE O RÓTULO DE IA EXPLICA, em cada lugar onde a Nyta gera conteúdo. Um texto só por lugar,
// para a explicação não variar de tela para tela.
//
// ⚠️ SÓ O QUE É VERDADE. A Nyta escreve com um modelo de linguagem (o da Maestra, configurado no
// Admin, então o nome dele não vai aqui); o diagnóstico REAL é do motor, e o plano é do método:
// nenhum dos dois leva este rótulo.

/** O conteúdo da explicação do rótulo de IA, no molde do Carbon for AI. */
export interface SobreAIA {
  /** O que a IA fez aqui, numa linha ("A Nyta escreveu a visão a partir das suas respostas"). */
  titulo: string;
  /** Como, em uma ou duas frases. */
  texto: string;
  /** Os detalhes que sustentam (de onde vieram os dados, o que a pessoa pode conferir). */
  detalhes?: { rotulo: string; valor: string }[];
  /** De onde vieram os dados (o molde do Carbon: artefatos e recursos). */
  fontes?: string[];
  /** O que se pode fazer a partir daqui (ver as respostas, abrir a conversa). */
  acoes?: import('react').ReactNode;
}

export const SOBRE_A_CONVERSA: SobreAIA = {
  titulo: 'Esta conversa é com uma IA',
  texto: 'A Nyta é a IA da Maestra. Tudo o que ela escreve aqui é gerado por um modelo de linguagem, a partir das suas perguntas e dos dados deste perfil. Ela pode errar: confira o que for importante.',
  detalhes: [
    { rotulo: 'O que ela lê', valor: 'Músicas, agenda, shows, plano de ação, planejamento, equipe e arquivos do perfil; quando precisa, o Chartmetric e a internet.' },
    { rotulo: 'O que ela grava', valor: 'Nada sem você. Toda mudança chega num cartão, e só é feita quando você confirma.' },
  ],
};

/**
 * A explicação de UMA resposta, pelo que ela usou:
 * - `leituras`: o que a Nyta foi ler para responder (a internet, o Chartmetric, o relatório), vindo
 *   dos avisos do servidor durante a resposta. `null` quando não se sabe (a conversa veio do
 *   histórico), e aí a explicação diz o que ela PODE usar, sem afirmar o que usou;
 * - o gráfico diz de onde vieram os números (o servidor calcula; o modelo não inventa número);
 * - a tabela diz que os números dela foram escritos pela IA.
 */
export const sobreDaResposta = (r: { leituras: string[] | null; fontesDosGraficos: string[]; temArquivo: boolean; temTabela: boolean }): SobreAIA => {
  // As leituras chegam como chaves (internet, chartmetric, relatorio) e as fontes dos gráficos como
  // nomes ("Chartmetric"): a comparação é pela chave, para a mesma fonte não sair duas vezes.
  const chave = (t: string) => t.toLowerCase().normalize('NFD').replace(/[^a-z]/g, '');
  const leituras = r.leituras ?? [];
  const graficosDe = new Set(r.fontesDosGraficos.map(chave));
  const fontes = r.leituras == null
    ? ['Os dados deste perfil na Maestra e, quando a pergunta pede, a internet e o Chartmetric']
    : [
      'Os dados deste perfil na Maestra',
      ...(leituras.includes('internet') ? ['A internet (busca na web)'] : []),
      ...(leituras.includes('chartmetric') ? [graficosDe.has('chartmetric') ? 'O Chartmetric (consulta e números do gráfico)' : 'O Chartmetric (consulta)'] : []),
      ...(leituras.includes('relatorio') ? ['Os dados do perfil (relatório gerado)'] : []),
      ...r.fontesDosGraficos.filter((f) => !(chave(f) === 'chartmetric' && leituras.includes('chartmetric'))).map((f) => `${f} (os números do gráfico)`),
      ...(r.temArquivo ? ['Os arquivos do perfil citados na resposta'] : []),
    ];
  const detalhes = [
    ...(r.fontesDosGraficos.length ? [{ rotulo: 'Os números do gráfico', valor: 'Calculados pela Maestra a partir da fonte, e não pelo modelo.' }] : []),
    ...(r.temTabela ? [{ rotulo: 'A tabela', valor: 'Os números da tabela foram escritos pela IA. Confira nos módulos antes de usar.' }] : []),
    { rotulo: 'Confira', valor: 'A IA pode errar. Antes de decidir algo importante, confira nos módulos.' },
  ];
  return {
    titulo: 'Resposta escrita pela Nyta',
    texto: 'Gerada por IA, com um modelo de linguagem, a partir da sua pergunta e dos dados deste perfil.',
    detalhes,
    fontes,
  };
};

/** O que cada ferramenta da Nyta faz, e como se desfaz (a verdade de cada uma, e não um molde). */
const O_QUE_A_FERRAMENTA_FAZ: Record<string, { faz: string; desfaz: string }> = {
  create_catalog_item: { faz: 'Cria uma música em Músicas.', desfaz: 'Sim: dá para apagar a música em Músicas.' },
  update_catalog_item: { faz: 'Muda os dados de uma música em Músicas.', desfaz: 'Em parte: o valor anterior não fica guardado; dá para editar de novo em Músicas.' },
  delete_catalog_item: { faz: 'Apaga uma música de Músicas.', desfaz: 'Não: apagar não se desfaz.' },
  create_event: { faz: 'Cria um compromisso na Agenda.', desfaz: 'Sim: dá para apagar o compromisso na Agenda.' },
  update_event: { faz: 'Muda um compromisso da Agenda.', desfaz: 'Em parte: o valor anterior não fica guardado; dá para editar de novo na Agenda.' },
  delete_event: { faz: 'Apaga um compromisso da Agenda.', desfaz: 'Não: apagar não se desfaz.' },
  create_team_member: { faz: 'Convida uma pessoa para a Equipe e manda o convite por e-mail.', desfaz: 'Em parte: dá para tirar a pessoa da Equipe, mas o e-mail já terá saído.' },
  update_team_member: { faz: 'Muda o acesso de uma pessoa da Equipe.', desfaz: 'Sim: dá para mudar de novo em Equipe.' },
  remove_team_member: { faz: 'Tira uma pessoa da Equipe.', desfaz: 'Em parte: dá para convidar a pessoa de novo.' },
  update_plan_task: { faz: 'Muda uma ação do Plano de ação.', desfaz: 'Em parte: o valor anterior não fica guardado; dá para editar de novo no Plano de ação.' },
  create_strategy: { faz: 'Cria uma estratégia no Plano de ação, com as ações e as tarefas.', desfaz: 'Sim: dá para arquivar a estratégia no Plano de ação.' },
  create_task: { faz: 'Acrescenta uma tarefa a uma ação do Plano de ação.', desfaz: 'Sim: dá para tirar a tarefa no Plano de ação.' },
  rename_strategy: { faz: 'Muda o nome de uma estratégia.', desfaz: 'Sim: dá para renomear de novo no Plano de ação.' },
  archive_strategy: { faz: 'Arquiva uma estratégia: ela sai do plano e da Agenda, e o que já foi feito fica guardado.', desfaz: 'Sim: dá para trazê-la de volta em Arquivadas, no Plano de ação.' },
  restore_strategy: { faz: 'Traz uma estratégia arquivada de volta ao plano.', desfaz: 'Sim: dá para arquivar de novo.' },
  update_strategic_planning: { faz: 'Muda um bloco do Planejamento estratégico.', desfaz: 'Em parte: o texto anterior não fica guardado; dá para editar de novo no Planejamento.' },
  rename_file: { faz: 'Muda o nome de um arquivo.', desfaz: 'Sim: dá para renomear de novo em Arquivos.' },
  delete_file: { faz: 'Apaga um arquivo do perfil.', desfaz: 'Não: apagar não se desfaz.' },
  create_text_file: { faz: 'Cria um arquivo de texto em Arquivos.', desfaz: 'Sim: dá para apagar o arquivo em Arquivos.' },
  update_text_file: { faz: 'Troca o texto inteiro de um arquivo.', desfaz: 'Não: o texto anterior não fica guardado.' },
};

const DO_ESTADO: Record<string, string> = {
  pendente: 'Nada é gravado sem você: ela só é feita se você confirmar.',
  executando: 'Você confirmou, e ela está sendo feita agora.',
  confirmada: 'Você confirmou esta ação.',
  feita: 'Você confirmou, e ela foi feita.',
  cancelada: 'Você cancelou: nada foi gravado.',
  falhou: 'Ela não foi feita: nada mudou.',
};

/** A explicação de uma ação proposta: o que ela faz, se dá para desfazer, e em que pé está. */
export const sobreDaAcao = (a: { ferramenta: string; estado: string; acoes?: import('react').ReactNode }): SobreAIA => {
  const f = O_QUE_A_FERRAMENTA_FAZ[a.ferramenta] ?? { faz: 'Grava no perfil.', desfaz: 'Confira no módulo onde ela grava.' };
  return {
    titulo: 'Ação proposta pela Nyta',
    texto: `A Nyta montou esta ação a partir da conversa. ${DO_ESTADO[a.estado] ?? ''}`.trim(),
    detalhes: [
      { rotulo: 'O que faz', valor: f.faz },
      // Cancelada ou que falhou não fez nada: não há o que desfazer.
      ...(a.estado === 'cancelada' || a.estado === 'falhou' ? [] : [{ rotulo: 'Dá para desfazer?', valor: f.desfaz }]),
      { rotulo: 'Registro', valor: 'A proposta e a sua decisão ficam guardadas nesta conversa.' },
    ],
    acoes: a.acoes,
  };
};

// ── O planejamento: os três textos que a IA escreve (o resto é do método) ──

export const SOBRE_A_VISAO: SobreAIA = {
  titulo: 'A Nyta escreveu a sua visão',
  texto: 'A IA juntou as suas respostas (até onde quer chegar, por quem quer ser reconhecido, como se define e o que falam de você) numa frase só. As escolhas são suas; a redação é da IA.',
  detalhes: [{ rotulo: 'Pode ajustar', valor: 'Mude o que quiser. A sugestão da Nyta fica guardada, e dá para voltar a ela.' }],
};

export const SOBRE_A_MISSAO: SobreAIA = {
  titulo: 'A Nyta escreveu a sua missão',
  texto: 'A IA juntou o que a sua carreira entrega, para quem, e o retorno que você quer numa frase só. As escolhas são suas; a redação é da IA.',
  detalhes: [{ rotulo: 'Pode ajustar', valor: 'Mude o que quiser. A sugestão da Nyta fica guardada, e dá para voltar a ela.' }],
};

export const SOBRE_O_PRESS_KIT: SobreAIA = {
  titulo: 'A Nyta escreveu este rascunho',
  texto: 'A IA leu o seu branding (arquétipo, palavra-chave, o que você é e o que não é, tom de voz) e o seu perfil, e escreveu um primeiro texto no seu tom. O que você já tinha escrito continua valendo.',
  detalhes: [
    { rotulo: 'O que ela não faz', valor: 'Não inventa prêmio, número nem show. O que ela cita vem dos destaques que você escreveu.' },
    { rotulo: 'Pode ajustar', valor: 'Mude o que quiser: ao editar, o texto passa a ser seu e esta marca sai.' },
  ],
};

export const SOBRE_O_RESUMO: SobreAIA = {
  titulo: 'A Nyta escreveu o resumo executivo',
  texto: 'A IA leu o seu planejamento inteiro (identidade, visão, missão, objetivos, SWOT e estratégias) e os dados das plataformas, e escreveu onde você está, as apostas principais e os próximos 90 dias.',
  detalhes: [
    { rotulo: 'O que é do método', valor: 'Os objetivos, a SWOT, as estratégias, a prioridade e o cronograma saem da metodologia da Maestra, e não da IA.' },
    { rotulo: 'Confira', valor: 'A IA pode errar. Confira os números e as datas antes de levar o resumo adiante.' },
    { rotulo: 'Pode ajustar', valor: 'Mude o que quiser. A sugestão da Nyta fica guardada, e dá para voltar a ela.' },
  ],
};

/**
 * A mesma explicação na página do Planejamento: quem edita ajusta pelo lápis do bloco; quem só lê
 * não recebe a promessa de editar.
 */
export const paraQuemLe = (sobre: SobreAIA, podeEditar: boolean): SobreAIA => ({
  ...sobre,
  detalhes: sobre.detalhes?.map((d) => (d.rotulo !== 'Pode ajustar' ? d : {
    ...d,
    valor: podeEditar
      ? 'Mude pelo lápis deste bloco. A sugestão da Nyta fica guardada, e dá para voltar a ela.'
      : 'Quem administra o perfil pode ajustar.',
  })),
});

export const SOBRE_A_RESPOSTA_GUIADA: SobreAIA = {
  titulo: 'A Nyta montou esta resposta',
  texto: 'A IA escreveu a resposta a partir do que você contou nas perguntas. Use, ajuste ou refaça as perguntas.',
};
